import { Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';
import { AttendanceService } from '../../services/attendance.service';

interface ImportedAttendanceRecord {
  personId: string;
  date: string;
  time: string;
  status: string;
  rawData: any;
}

@Component({
  selector: 'app-attendance-import',
  templateUrl: './attendance-import.component.html',
  styleUrls: ['./attendance-import.component.scss']
})
export class AttendanceImportComponent {
  loading = false;
  importSummary: string = '';
  importedRecords: ImportedAttendanceRecord[] = [];

  constructor(private snackBar: MatSnackBar, private attendanceService: AttendanceService) {}

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    this.loading = true;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
      // Parse rows and map to attendance records
      const attendanceRecords = this.parseRows(rows);
      this.importedRecords = attendanceRecords;
      // Call backend API to import
      this.importAttendanceRecords(attendanceRecords);
    };
    reader.readAsArrayBuffer(file);
  }

  parseRows(rows: any[][]): ImportedAttendanceRecord[] {
    const records: ImportedAttendanceRecord[] = [];
    if (rows.length < 2) {
      console.warn('Excel file has insufficient rows');
      return records;
    }

    // Find the header row dynamically (look for a row with both 'Person ID' and 'Date')
    let headerRowIdx = -1;
    let header: string[] = [];
    for (let i = 0; i < Math.min(10, rows.length); i++) {
      const row = rows[i].map((h: any) => h?.toString().trim().toLowerCase());
      if (row.includes('person id') && row.includes('date')) {
        headerRowIdx = i;
        header = row;
        break;
      }
    }
    if (headerRowIdx === -1) {
      console.error('Could not find header row with both "Person ID" and "Date" in the first 10 rows.');
      return records;
    }
    console.log(`Detected header row at index ${headerRowIdx}:`, header);
    const personIdIdx = header.indexOf('person id');
    const dateIdx = header.indexOf('date');
    // Find all Clock In/Out column pairs
    const clockPairs: { inIdx: number; outIdx: number; }[] = [];
    for (let i = 0; i < header.length; i++) {
      if (header[i] === 'clock in') {
        // Find the next 'clock out' after this
        for (let j = i + 1; j < header.length; j++) {
          if (header[j] === 'clock out') {
            clockPairs.push({ inIdx: i, outIdx: j });
            break;
          }
        }
      }
    }
    console.log('Detected clock in/out pairs:', clockPairs);
    if (clockPairs.length === 0) {
      console.error('No Clock In/Out column pairs found.');
      return records;
    }

    for (let i = headerRowIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) {
        console.warn(`Skipping row ${i + 1}: Empty row`);
        continue;
      }
      const personId = row[personIdIdx]?.toString().trim() || '';
      const date = row[dateIdx]?.toString().trim() || '';
      if (!personId || !date) {
        console.warn(`Skipping row ${i + 1}: Missing Person ID or Date`, { personId, date, row });
        continue;
      }

      let foundValid = false;
      for (const pair of clockPairs) {
        const clockIn = row[pair.inIdx]?.toString().trim();
        const clockOut = row[pair.outIdx]?.toString().trim();
        // Add Clock In record
        if (clockIn) {
          const dateTimeStr = `${date} ${clockIn}`;
          if (!isNaN(Date.parse(dateTimeStr))) {
            records.push({
              personId,
              date,
              time: clockIn,
              status: 'ClockIn',
              rawData: row
            });
            foundValid = true;
          } else {
            console.warn(`Skipping row ${i + 1}: Invalid Clock In date/time`, { date, clockIn, dateTimeStr, row });
          }
        }
        // Add Clock Out record
        if (clockOut) {
          const dateTimeStr = `${date} ${clockOut}`;
          if (!isNaN(Date.parse(dateTimeStr))) {
            records.push({
              personId,
              date,
              time: clockOut,
              status: 'ClockOut',
              rawData: row
            });
            foundValid = true;
          } else {
            console.warn(`Skipping row ${i + 1}: Invalid Clock Out date/time`, { date, clockOut, dateTimeStr, row });
          }
        }
      }
      if (!foundValid) {
        console.warn(`Skipping row ${i + 1}: No valid Clock In/Out times found`, { row });
      }
    }
    console.log(`Parsed ${records.length} valid records from ${rows.length - (headerRowIdx + 1)} data rows`);
    return records;
  }

  importAttendanceRecords(records: ImportedAttendanceRecord[]) {
    if (records.length === 0) {
      this.loading = false;
      this.importSummary = 'No valid records found to import';
      this.snackBar.open(this.importSummary, 'Close', { duration: 3000 });
      return;
    }

    // Convert to backend format
    const importData = records.map(record => ({
      personId: record.personId,
      date: record.date,
      time: record.time,
      status: record.status
    }));

    this.attendanceService.importAttendanceRecords(importData).subscribe({
      next: (response) => {
        this.loading = false;
        this.importSummary = `Successfully imported ${response.importedCount} records. ${response.skippedCount} duplicates skipped.`;
        this.snackBar.open(this.importSummary, 'Close', { duration: 5000 });
      },
      error: (error) => {
        this.loading = false;
        this.importSummary = `Import failed: ${error.error?.message || error.message}`;
        this.snackBar.open(this.importSummary, 'Close', { duration: 5000 });
      }
    });
  }
} 